import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

export default function HeroStatStrip() {
  const { t } = useTranslation();
  const [cms, setCms] = useState({});
  useEffect(() => {
    api.get("/content").then(({ data }) => setCms(data || {})).catch(() => {});
  }, []);
  // `accent` flag — 'up' = green ▲ growth marker, 'lightning' = brand
  // Zap icon for the Instant onboarding stat (matches the "Instant"
  // narrative — fast, electric, immediate). Different lever from the
  // bullish ▲ so visitors instantly distinguish the two.
  const ITEMS = [
    { value: cms.hero_strip_v1 || "90%",     label: cms.hero_strip_l1 || t("heroStrip.accountGains"), accent: "up" },
    { value: cms.hero_strip_v2 || "$250K",   label: cms.hero_strip_l2 || t("heroStrip.maxCapital"),   accent: "up" },
    { value: cms.hero_strip_v3 || "78+",     label: cms.hero_strip_l3 || t("heroStrip.countries"),    accent: "up" },
    { value: cms.hero_strip_v4 || t("heroStrip.instantValue", "Instant"), label: cms.hero_strip_l4 || t("heroStrip.onboarding"),   accent: "lightning" },
  ];
  return (
    <section data-testid="hero-stat-strip" className="relative py-6 lg:py-7 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-5 items-stretch">
          {ITEMS.map((it, i) => {
            // Every cell is centered on every breakpoint so values and
            // labels line up symmetrically across the strip. The dividers
            // between columns provide the visual rhythm without needing
            // edge-hugging alignment.
            const align = "items-center text-center md:items-center md:text-center";
            return (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className={`relative flex flex-col justify-between ${align}`}
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-display text-3xl sm:text-4xl lg:text-7xl xl:text-[5rem] font-medium leading-[1.05] mb-2 lg:mb-2.5 text-gradient-brand flex items-center gap-1.5 lg:gap-3 whitespace-nowrap">
                  {it.value}
                  {it.accent === "up" && (
                    <TrendingUp size={16} strokeWidth={2.5} className="lg:!w-6 lg:!h-6 text-[#54d97c] animate-pulse shrink-0" />
                  )}
                  {it.accent === "lightning" && (
                    <Zap
                      size={16}
                      strokeWidth={2.6}
                      className="lg:!w-6 lg:!h-6 text-brand animate-pulse shrink-0"
                      fill="currentColor"
                      style={{ filter: "drop-shadow(0 0 4px color-mix(in srgb, var(--brand) 70%, transparent))" }}
                    />
                  )}
                </div>
                <div className="font-mono text-[10px] lg:text-[13px] text-zinc-500 tracking-[0.18em] lg:tracking-[0.22em] uppercase whitespace-nowrap">
                  {it.label}
                </div>
                {i < ITEMS.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-14 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
